import React, { useRef, useEffect, useState } from 'react'
import Guacamole from 'guacamole-common-js'
import './styles.scss'

const GuacamoleConsole = ({ vmId, resourceType, session, onSyncClipboard }) => {
  const displayRef = useRef(null)
  const inputRef = useRef(null) // { client, disabled }: teclado/ratón del cliente actual
  const onSyncClipboardRef = useRef(onSyncClipboard)
  onSyncClipboardRef.current = onSyncClipboard

  // Como SRA: al hacer clic en la consola se envía el portapapeles local a la máquina,
  // así ya está en la máquina cuando el usuario pulsa Ctrl+V (RDP, SSH, VNC)
  useEffect(() => {
    const displayElement = displayRef.current
    if (!displayElement) return undefined
    const onMouseDown = () => {
      if (onSyncClipboardRef.current) onSyncClipboardRef.current()
    }
    displayElement.addEventListener('mousedown', onMouseDown, true)
    return () => displayElement.removeEventListener('mousedown', onMouseDown, true)
  }, [])

  const [, setErrorMessage] = useState(null)

  useEffect(() => {
    const displayElement = displayRef.current
    let removeClickListener = null

    if (session && session.client && displayElement) {
      const client = session.client
      const isConnected = session.isConnected
      const guacDisplay = client.getDisplay().getElement()

      const focusDisplay = () => {
        if (guacDisplay && typeof guacDisplay.focus === 'function') {
          guacDisplay.tabIndex = 0
          guacDisplay.focus()
        }
      }

      const drawConsole = () => {
        if (!displayElement.contains(guacDisplay)) {
          // Quitar el canvas de un cliente anterior (cada reconexión crea un cliente nuevo)
          while (displayElement.firstChild) {
            displayElement.removeChild(displayElement.firstChild)
          }
          displayElement.appendChild(guacDisplay)
        }
        displayElement.addEventListener('click', focusDisplay)
        removeClickListener = () => displayElement.removeEventListener('click', focusDisplay)

        displayElement.onerror = (error) => {
          setErrorMessage(`Display error: ${error.message}`)
        }
      }

      // Un solo teclado y un solo ratón por cliente. Nunca se recrean por render ni se dejan
      // handlers en null: Guacamole.Keyboard sigue escuchando el elemento y llama onkeyup sin validar.
      const hardware = () => {
        if (inputRef.current && inputRef.current.client === client) return
        if (inputRef.current) inputRef.current.disabled = true // cliente anterior

        const input = { client, disabled: false }
        inputRef.current = input

        const mouse = new Guacamole.Mouse(guacDisplay)
        mouse.onmousedown =
          mouse.onmouseup =
          mouse.onmousemove =
            (mouseState) => {
              if (!input.disabled) client.sendMouseState(mouseState)
            }

        const keyboard = new Guacamole.Keyboard(guacDisplay)
        keyboard.onkeydown = (keysym) => {
          if (input.disabled) return true
          client.sendKeyEvent(1, keysym) // Envía el evento de tecla presionada
          return false
        }
        keyboard.onkeyup = (keysym) => {
          if (!input.disabled) client.sendKeyEvent(0, keysym) // Envía el evento de tecla soltada
        }
      }

      const adjustDisplaySize = () => {
        if (!client || !client.getDisplay) {
          console.error('Guacamole client o su display no está listo.')
          return
        }

        if (!guacDisplay) {
          console.error('guacDisplay no está inicializado.')
          return
        }
        // Solo ajustar el tamaño si el display tiene dimensiones válidas
        let width = displayElement.offsetWidth
        let height = displayElement.offsetHeight

        if (width > 0 && height > 0) {
          client.sendSize(width, height) // Ajustar tamaño en función de la resolución
          guacDisplay.style.width = `${width}px`
          guacDisplay.style.height = `${height}px`

          // Asegurar que guacDisplay tenga el foco después del ajuste de tamaño
          if (guacDisplay && typeof guacDisplay.focus === 'function') {
            guacDisplay.focus()
          }
        } else {
          // Intentar reajustar cuando las dimensiones sean válidas
          setTimeout(adjustDisplaySize, 100) // Reintento después de un breve retardo
        }
      }

      if (isConnected) {
        adjustDisplaySize()
        hardware()
        drawConsole()
      }
    }

    return () => {
      if (removeClickListener) removeClickListener()
    }
  }, [session, vmId])

  // Al desmontar, desactivar teclado y ratón (sin poner handlers en null)
  useEffect(
    () => () => {
      if (inputRef.current) inputRef.current.disabled = true
    },
    []
  )

  return (
    <div className="remote-client-container">
      <div ref={displayRef} tabIndex={0} className="remote-client-display" />
    </div>
  )
}

export default GuacamoleConsole
